
import React, { useState } from "react";
import axios from "axios";
import "./Volunteer.css";
import Swal from "sweetalert2";

function VolunteerForm() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
    location: "",
    skills: "",
    description: "",
  });

  const [loading, setLoading] = useState(false);
  const [cv, setCv] = useState(null);

  // =====================================================
  // INPUT CHANGE
  // =====================================================

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =====================================================
  // FILE CHANGE
  // =====================================================

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setCv(file);
  };

  // =====================================================
  // SUBMIT
  // =====================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submit
    if (loading) return;

    // ===================================================
    // TOKEN CHECK
    // ===================================================

    const token = localStorage.getItem("token");

    if (!token) {
      Swal.fire({
        icon: "warning",
        title: "Login Required",
        text: "Please login before submitting the volunteer form.",
        confirmButtonColor: "#1e4620",
      });

      return;
    }

    // ===================================================
    // FRONTEND VALIDATION
    // ===================================================

    if (!formData.fullName.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Full Name Required",
        text: "Please enter your full name.",
      });
      return;
    }

    if (!formData.email.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Email Required",
        text: "Please enter your email.",
      });
      return;
    }

    if (!formData.phone.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Phone Required",
        text: "Please enter your phone number.",
      });
      return;
    }

    if (!formData.address.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Address Required",
        text: "Please enter your address.",
      });
      return;
    }

    if (!formData.location.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Location Required",
        text: "Please enter your city/location.",
      });
      return;
    }

    if (!formData.skills.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Skills Required",
        text: "Please enter your skills.",
      });
      return;
    }

    if (!cv) {
      Swal.fire({
        icon: "warning",
        title: "CV Required",
        text: "Please upload your CV.",
      });
      return;
    }

    // ===================================================
    // SUBMIT
    // ===================================================

    setLoading(true);

    try {
      const data = new FormData();

      data.append("fullName", formData.fullName.trim());
      data.append("email", formData.email.trim());
      data.append("phone", formData.phone.trim());
      data.append("address", formData.address.trim());
      data.append("location", formData.location.trim());

      // Backend skills ko parse kar raha hai
      data.append("skills", formData.skills.trim());

      data.append("description", formData.description.trim());

      // IMPORTANT:
      // Backend multer.single("cvPath") use kar raha hai
      data.append("cvPath", cv);

      console.log("====================================");
      console.log("VOLUNTEER FORM SUBMIT");
      console.log("Full Name:", formData.fullName);
      console.log("Email:", formData.email);
      console.log("Phone:", formData.phone);
      console.log("Address:", formData.address);
      console.log("Location:", formData.location);
      console.log("Skills:", formData.skills);
      console.log("CV:", cv.name);
      console.log("TOKEN:", token ? "YES" : "NO");
      console.log("====================================");

      const response = await axios.post(
        "/api/volunteer/submit",
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("VOLUNTEER RESPONSE:", response.data);

      Swal.fire({
        icon: "success",
        title: "Registration Submitted",
        text:
          response.data?.message ||
          "Thank you for registering as a volunteer!",
        confirmButtonColor: "#1e4620",
      });

      // =================================================
      // RESET FORM
      // =================================================

      setFormData({
        fullName: "",
        email: "",
        phone: "",
        address: "",
        location: "",
        skills: "",
        description: "",
      });

      setCv(null);

      // File input manually clear karne ke liye
      const fileInput = document.getElementById("cvPath");

      if (fileInput) {
        fileInput.value = "";
      }
    } catch (error) {
      console.error("VOLUNTEER SUBMIT ERROR:", error);

      const status = error.response?.status;
      const backendMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Something went wrong. Please try again.";

      console.log("STATUS:", status);
      console.log("BACKEND MESSAGE:", backendMessage);

      // =================================================
      // DUPLICATE VOLUNTEER
      // =================================================

      if (
        backendMessage
          .toLowerCase()
          .includes("email already registered")
      ) {
        Swal.fire({
          icon: "warning",
          title: "Already Registered",
          text: "This email is already registered as a volunteer.",
          confirmButtonColor: "#1e4620",
        });

        return;
      }

      // =================================================
      // CV ERROR
      // =================================================

      if (
        backendMessage
          .toLowerCase()
          .includes("cv upload is required")
      ) {
        Swal.fire({
          icon: "warning",
          title: "CV Required",
          text: "Please upload your CV before submitting.",
          confirmButtonColor: "#1e4620",
        });

        return;
      }

      // =================================================
      // REQUIRED FIELDS
      // =================================================

      if (
        backendMessage
          .toLowerCase()
          .includes("all required fields")
      ) {
        Swal.fire({
          icon: "warning",
          title: "Missing Information",
          text: "Please fill all required fields.",
          confirmButtonColor: "#1e4620",
        });

        return;
      }

      // =================================================
      // GENERAL ERROR
      // =================================================

      Swal.fire({
        icon: "error",
        title: `Registration Failed ${status ? `(${status})` : ""}`,
        text: backendMessage,
        confirmButtonColor: "#d33",
      });
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="volunteer-container">
      <div className="volunteer-card">
        <h1>Volunteer Registration</h1>

        <p className="subtitle">
          Join us as a volunteer and help the community
        </p>

        <form
          className="volunteer-form"
          onSubmit={handleSubmit}
          encType="multipart/form-data"
        >
          {/* FULL NAME */}
          <div className="input-group">
            <label>Full Name</label>

            <input
              name="fullName"
              type="text"
              value={formData.fullName}
              placeholder="Enter your full name"
              required
              onChange={handleInputChange}
            />
          </div>

          {/* EMAIL */}
          <div className="input-group">
            <label>Email</label>

            <input
              name="email"
              type="email"
              value={formData.email}
              placeholder="Enter your email"
              required
              onChange={handleInputChange}
            />
          </div>

          {/* PHONE */}
          <div className="input-group">
            <label>Phone Number</label>

            <input
              name="phone"
              type="tel"
              value={formData.phone}
              placeholder="Enter your phone number"
              required
              onChange={handleInputChange}
            />
          </div>

          {/* ADDRESS */}
          <div className="input-group">
            <label>Address</label>

            <input
              name="address"
              type="text"
              value={formData.address}
              placeholder="Enter your address"
              required
              onChange={handleInputChange}
            />
          </div>

          {/* LOCATION */}
          <div className="input-group">
            <label>Location</label>

            <input
              name="location"
              type="text"
              value={formData.location}
              placeholder="Enter your city/location"
              required
              onChange={handleInputChange}
            />
          </div>

          {/* SKILLS */}
          <div className="input-group">
            <label>Skills</label>

            <input
              name="skills"
              type="text"
              value={formData.skills}
              placeholder="e.g. Plantation, Teaching, Social Media"
              required
              onChange={handleInputChange}
            />
          </div>

          {/* DESCRIPTION */}
          <div className="input-group">
            <label>Description</label>

            <textarea
              name="description"
              rows="4"
              value={formData.description}
              placeholder="Write something about yourself"
              onChange={handleInputChange}
            />
          </div>

          {/* CV */}
          <div className="input-group">
            <label>Upload CV</label>

            <input
              id="cvPath"
              type="file"
              accept=".pdf,.doc,.docx"
              required
              onChange={handleFileChange}
            />
          </div>

          {/* SUBMIT */}
          <button
            type="submit"
            className="submit-btn"
            disabled={loading}
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default VolunteerForm;

